import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { getStickyPurchaseState, getOptionsScrollOffset } from './sticky-purchase-state.js?v=20260926-options';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Guides shoppers to options before the buy buttons, then supports native cart actions after them.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  #overlayObserver = null;
  #visibilityFrame = null;
  #stickyBarHeight = 0;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('scroll', this.#queueVisibilityUpdate, { signal, passive: true });
    window.addEventListener('resize', this.#queueVisibilityUpdate, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', this.#queueVisibilityUpdate, { signal, passive: true });
    window.visualViewport?.addEventListener('scroll', this.#queueVisibilityUpdate, { signal, passive: true });
    this.#overlayObserver = new MutationObserver(this.#handleOverlayMutation);
    this.#overlayObserver.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['open', 'scroll-lock', 'hidden', 'aria-hidden', 'style', 'class'],
    });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#abortController.abort();
    if (this.#visibilityFrame) cancelAnimationFrame(this.#visibilityFrame);
    this.#visibilityFrame = null;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!this.#targetAddToCartButton) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#updateVisibility, { threshold: [0, 1] });
    this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);
    const options = this.#getOptionsTarget();
    if (options) this.#buyButtonsIntersectionObserver.observe(options);
    if (footer) this.#buyButtonsIntersectionObserver.observe(footer);
    this.#updateVisibility();
  }

  #queueVisibilityUpdate = () => {
    if (this.#visibilityFrame) return;
    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      this.#updateVisibility();
    });
  };

  #isOverlayOpen() {
    if (document.querySelector('dialog[open], details[open][scroll-lock], .z-header details[open]') ||
      document.documentElement.hasAttribute('scroll-lock')) return true;
    return Array.from(document.querySelectorAll('[role="dialog"]:not([aria-hidden="true"]), [role="alertdialog"]:not([aria-hidden="true"])'))
      .some((dialog) => dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden');
  }

  #handleOverlayMutation = () => {
    if (this.#isOverlayOpen()) {
      this.dataset.purchaseState = 'hidden';
      this.#hideStickyBar();
      return;
    }
    this.#queueVisibilityUpdate();
  };

  #updateVisibility = () => {
    if (this.#isOverlayOpen()) {
      this.dataset.purchaseState = 'hidden';
      this.#hideStickyBar();
      return 'hidden';
    }
    const bounds = this.#getProductForm()?.querySelector('[ref="addToCartButton"]')?.getBoundingClientRect();
    const viewport = this.#getViewport();
    this.#stickyBarHeight = Math.max(this.#stickyBarHeight, this.refs.stickyBar.getBoundingClientRect().height || 0);
    const optionsBounds = this.#getOptionsTarget()?.getBoundingClientRect();
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const footerBounds = footer?.getBoundingClientRect();
    const footerVisible = Boolean(footerBounds && footerBounds.top < viewport.bottom && footerBounds.bottom > viewport.top);
    const state = getStickyPurchaseState(bounds, viewport, footerVisible, false, optionsBounds, this.#stickyBarHeight);
    this.dataset.purchaseState = state;
    const available = state === 'before' ? this.dataset.productAvailable === 'true' : this.dataset.variantAvailable === 'true';
    if ((state === 'before' || state === 'past') && available) this.#showStickyBar();
    else this.#hideStickyBar();
    return state;
  };

  #getViewport() {
    const visual = window.visualViewport;
    const top = visual?.offsetTop ?? 0;
    const bottom = Math.min(window.innerHeight, top + (visual?.height ?? window.innerHeight));
    const offset = Math.max(0, window.innerHeight - bottom);
    const placement = `calc(16px + env(safe-area-inset-bottom) + ${offset}px)`;
    if (this.refs.stickyBar.style.getPropertyValue('bottom') !== placement) {
      this.refs.stickyBar.style.setProperty('bottom', placement);
    }
    const safeArea = Math.max(0, (parseFloat(getComputedStyle(this.refs.stickyBar).bottom) || 16) - offset - 16);
    return { top, bottom: bottom - safeArea };
  }

  #getOptionsTarget() {
    const picker = this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const sizeIndex = this.dataset.sizeOptionIndex;
    const sizeOption = sizeIndex ? picker?.querySelectorAll('.variant-option')[Number(sizeIndex)] : null;
    return sizeOption ?? picker;
  }

  #scrollToOptions() {
    const target = this.#getOptionsTarget() ?? this.#getProductForm();
    if (!target) return;
    const control = target?.querySelector('input:checked:not([disabled]), select:not([disabled])') ??
      target?.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled])');
    control?.focus({ preventScroll: true });
    const buttonBounds = this.#getProductForm()?.querySelector('[ref="addToCartButton"]')?.getBoundingClientRect();
    const pickerBounds = target.closest('variant-picker')?.getBoundingClientRect();
    const top = getOptionsScrollOffset(target.getBoundingClientRect(), buttonBounds, this.#getViewport(), this.#stickyBarHeight, pickerBounds);
    window.scrollBy({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const state = this.#updateVisibility();
    if (state === 'before') {
      this.#scrollToOptions();
      return;
    }
    if (state !== 'past') return;
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled || this.dataset.variantAvailable !== 'true') return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.dataset.productAvailable = newStickyAddToCart.dataset.productAvailable;
    this.dataset.sizeOptionIndex = newStickyAddToCart.dataset.sizeOptionIndex;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#setupIntersectionObserver();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.inert = false;
    if (stickyBar.getAttribute('aria-hidden') !== 'false') stickyBar.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.inert = true;
    if (stickyBar.getAttribute('aria-hidden') !== 'true') stickyBar.setAttribute('aria-hidden', 'true');
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
